import { Link } from "react-router-dom";
import { useUiLang } from "../../stores/uiLang";

function PrivacyPolicyPageEn() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Protecting student privacy is our priority. This policy governs how
          Project Explore collects, uses, and protects personal information.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="text-xs text-gray-500">
          <strong>Operator:</strong> Startup Smartup, 2380 California Street
          #402, San Francisco, CA 94115 &nbsp;|&nbsp;{" "}
          <strong>Effective Date:</strong> May 27, 2026
        </p>

        {/* Section 1 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            1. Information We Collect
          </h2>
          <p className="mb-3">
            Project Explore is a K-5 bilingual educational platform operated
            under contract with schools and districts. All student accounts are
            provisioned by the contracting school or district — students do not
            self-register with personal information.
          </p>
          <p className="font-medium mb-2">Student information we collect:</p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Category</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Data</th>
                  <th className="border border-gray-200 px-3 py-2 text-left font-medium">Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Identity</td>
                  <td className="border border-gray-200 px-3 py-2">First name, last name</td>
                  <td className="border border-gray-200 px-3 py-2">Required for all student accounts</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Enrollment</td>
                  <td className="border border-gray-200 px-3 py-2">Grade level (K-5)</td>
                  <td className="border border-gray-200 px-3 py-2">Required; used to assign appropriate content tiers</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Contact</td>
                  <td className="border border-gray-200 px-3 py-2">Email address</td>
                  <td className="border border-gray-200 px-3 py-2">Collected for students in grade 3 and above, only where provided by the district</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Educational activity</td>
                  <td className="border border-gray-200 px-3 py-2">Assignment responses and submissions</td>
                  <td className="border border-gray-200 px-3 py-2">Used for progress tracking and teacher review</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Educational activity</td>
                  <td className="border border-gray-200 px-3 py-2">Video viewing analytics</td>
                  <td className="border border-gray-200 px-3 py-2">Used for progress tracking and district reporting</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Educational feedback</td>
                  <td className="border border-gray-200 px-3 py-2">AI-generated feedback on open-response items</td>
                  <td className="border border-gray-200 px-3 py-2">Tied to assignment submissions; reviewed by teachers</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Accommodations</td>
                  <td className="border border-gray-200 px-3 py-2">IEP/504 flags</td>
                  <td className="border border-gray-200 px-3 py-2">Provided by the district; used to deliver accessibility scaffolding</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className="border border-gray-200 px-3 py-2">Parent/guardian</td>
                  <td className="border border-gray-200 px-3 py-2">Contact information</td>
                  <td className="border border-gray-200 px-3 py-2">Where provided by the district for notification purposes</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="font-medium mb-2">Teacher and administrator information we collect:</p>
          <p className="mb-3">
            Name and professional email address, for account creation, class
            management, and platform communication.
          </p>
          <p className="font-medium mb-2">What we do NOT collect from students:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Social Security numbers</li>
            <li>Home addresses</li>
            <li>Biometric identifiers</li>
            <li>Geolocation data</li>
            <li>Financial information</li>
            <li>Health information beyond IEP/504 flags provided by the school</li>
            <li>Behavioral advertising data or interest-based profiles</li>
            <li>Device identifiers used for advertising</li>
          </ul>
        </div>

        {/* Section 2 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. How We Use Information
          </h2>
          <p className="mb-3">
            Student data is used solely to deliver the Project Explore
            educational experience. Specifically:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li><strong>Educational delivery:</strong> Providing grade-level, bilingual (English/Spanish) video content and activities to students</li>
            <li><strong>Progress tracking:</strong> Enabling teachers and administrators to monitor student completion and mastery</li>
            <li><strong>Teacher review:</strong> Enabling teachers to read and respond to student open-response submissions</li>
            <li><strong>AI-generated feedback:</strong> Generating formative feedback on student responses using the platform's AI features</li>
            <li><strong>District reporting:</strong> Providing completion and progress data to district administrators for program evaluation</li>
            <li><strong>Accessibility scaffolding:</strong> Delivering IEP/504-appropriate content variants where applicable</li>
          </ul>
          <p className="font-semibold">
            Student data is never used for advertising, marketing, product
            development, or any purpose unrelated to educational delivery.
          </p>
        </div>

        {/* Section 3 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Sharing Information with Third Parties
          </h2>
          <p className="mb-3">
            Project Explore does not sell, rent, or share student data with
            third parties for commercial purposes. Student data is shared only
            with the following processors, each operating under a data
            processing agreement that prohibits use of student data for any
            purpose other than providing the contracted service:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2 mb-4">
            <li><strong>Supabase</strong> — database hosting, authentication, and serverless functions</li>
            <li><strong>Cloudflare (R2 + AutoRAG)</strong> — static asset storage and AI-powered support</li>
            <li><strong>Vimeo</strong> — video hosting and delivery</li>
            <li><strong>OpenAI</strong> — open-question grading and text-to-speech narration. DPA in place; API terms prohibit use of customer data, including student personal information, for model training. No student personal information is retained beyond the duration of the API request.</li>
            <li><strong>Anthropic</strong> — AI support features via Claude. DPA in place (incorporated into Commercial Terms of Service); API terms prohibit use of customer data, including student personal information, for model training. No student personal information is retained beyond the duration of the API request.</li>
            <li><strong>Google Cloud</strong> — Google Classroom integration, where used by a district</li>
            <li><strong>Clever</strong> — student single sign-on, where used by a district</li>
            <li><strong>SendGrid (Twilio SendGrid)</strong> — transactional email for teacher and administrator invitations (not used for student email)</li>
            <li><strong>ElevenLabs, Deepgram, LiveKit</strong> — voice interview feature, where used</li>
          </ul>
          <p className="mb-4">
            We also share data back to the contracting school and district.
          </p>
          <p className="mb-2">
            <strong>Law Enforcement Requests:</strong> Project Explore responds
            only to valid legal process (subpoena, court order, or other binding
            legal requirement). Where legally permissible, Project Explore will
            notify the contracting district before disclosing any student
            personal information in response to such a request and will disclose
            only the minimum necessary information.
          </p>
          <p>
            <strong>Data Location:</strong> All student personal information is
            stored within the United States. Project Explore's primary
            infrastructure providers — Supabase, Cloudflare, and Vimeo — maintain
            U.S. data center facilities for Project Explore's deployment.
          </p>
        </div>

        {/* Section 4 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. Parental Rights and Data Deletion Requests
          </h2>
          <p className="mb-3">
            Parents and guardians may request to review, correct, or delete
            their child's data at any time by:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2 mb-3">
            <li>Contacting their child's school administrator, who can export or delete records through the teacher dashboard; or</li>
            <li>Contacting Project Explore support directly at{" "}
              <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                privacy@startupsmartup.com
              </a>
            </li>
          </ol>
          <p>
            Project Explore will verify the requestor's identity through the
            contracting school before taking action. Requests will be fulfilled
            within 45 days unless retention is legally required.
          </p>
        </div>

        {/* Section 5 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Data Retention Policy
          </h2>
          <p className="mb-3">
            Student data is retained for the duration of the student's active
            enrollment in a Project Explore class. Project Explore will purge
            inactive student data within 90 days of the end of the school year
            for students who are no longer enrolled.
          </p>
          <p className="mb-3">
            Teacher data is retained while the teacher is active in any district
            and purged 365 days after their last recorded activity.
          </p>
          <p className="mb-3">
            Audit logs are retained for 7 years as required by certain state
            district compliance requirements. Backups are maintained on a
            30-day rolling cycle and purged within 30 days after the
            corresponding data is removed from production.
          </p>
          <p>
            <strong>Contract Termination:</strong> Upon termination of the
            district contract, Project Explore will, at the District's election,
            return all student personal information to the District or securely
            delete it within thirty (30) days of termination. Districts may
            request a certificate of deletion confirming that all student
            personal information has been removed from Project Explore's systems.
          </p>
        </div>

        {/* Section 6 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Breach Response Plan
          </h2>
          <p className="mb-3">
            In the event of a data breach, Project Explore will:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Investigate the source and extent of the breach</li>
            <li>Contain the incident by securing affected systems and revoking compromised credentials</li>
            <li>Test the fix to ensure the vulnerability is resolved</li>
            <li>Alert affected districts and, through them, affected families within 72 hours of confirming a breach</li>
            <li>Notify applicable regulatory authorities as required by law</li>
            <li>Execute long-term cleanup and security improvements</li>
            <li>Publish a post-incident report within 30 days of incident closure</li>
          </ul>
          <p>
            See the full{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Incident Response Policy
            </Link>{" "}
            for details.
          </p>
        </div>

        {/* Section 7 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Security of Your Information
          </h2>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li><strong>Encryption at rest:</strong> All student data is encrypted at rest using AES-256</li>
            <li><strong>Encryption in transit:</strong> TLS 1.2 or higher is enforced on all connections</li>
            <li><strong>Access controls:</strong> Row-level security policies in the database isolate data by district, school, class, and student — no cross-tenant data access is possible</li>
            <li><strong>Authentication:</strong> Multi-factor authentication is required for district and platform administrators</li>
            <li><strong>Backups:</strong> 30-day rolling backups with point-in-time recovery capability</li>
          </ul>
          <p>
            No platform can guarantee 100% security, but we are committed to
            protecting student data using industry-standard practices appropriate
            to the sensitivity of educational records.
          </p>
        </div>

        {/* Section 8 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            8. Automatically Collected Information
          </h2>
          <p className="mb-2">
            Project Explore does not automatically collect information about
            your device, browser, or location for advertising or profiling
            purposes. We do not use advertising pixels, behavioral tracking
            scripts, or data-broker integrations.
          </p>
          <p>
            Platform-level technical logs (error logs, authentication events,
            edge function requests) are collected for operational and security
            monitoring purposes only and are not tied to individual student
            identity profiles.
          </p>
        </div>

        {/* Section 9 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            9. Tracking User Behavior
          </h2>
          <p>
            Our platform does not track user behavior for advertising,
            customization of advertising, or behavioral analysis. All activity
            data collected (video progress, assignment completion, assessment
            scores) is used exclusively for educational delivery and reporting
            to the contracting school and district.
          </p>
        </div>

        {/* Section 10 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            10. Changes to This Policy
          </h2>
          <p>
            We may update this Privacy Policy from time to time. Material
            changes will be announced through the platform with at least 30
            days' notice before taking effect. Continued use of the platform
            after the effective date constitutes acceptance of the revised
            policy. A history of material changes is maintained in our
            Compliance Changelog.
          </p>
        </div>

        {/* Additional Compliance Statements */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            Additional Compliance Statements
          </h2>
          <p className="mb-2">
            Project Explore maintains separate compliance statements for
            specific federal and state requirements:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              <Link to="/privacy#coppa" className="text-indigo-600 underline">
                COPPA Addendum
              </Link>{" "}
              — Children's Online Privacy Protection Act
            </li>
            <li>
              <Link to="/ferpa" className="text-indigo-600 underline">
                FERPA Statement
              </Link>{" "}
              — Family Educational Rights and Privacy Act
            </li>
            <li>
              <Link to="/legal/sopipa" className="text-indigo-600 underline">
                California SOPIPA Acknowledgment
              </Link>{" "}
              — Cal. Bus. &amp; Prof. Code §§ 22584–22585
            </li>
            <li>
              <Link to="/acceptable-use" className="text-indigo-600 underline">
                Acceptable Use Policy
              </Link>
            </li>
            <li>
              <Link to="/legal/incident-response" className="text-indigo-600 underline">
                Incident Response Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            Contact Information
          </h2>
          <p className="mb-2">
            If you have any questions or concerns regarding our privacy
            practices, please contact:
          </p>
          <p>
            <strong>Justin Ghiglia</strong>
            <br />
            Project Explore / Startup Smartup
            <br />
            2380 California Street, #402
            <br />
            San Francisco, CA 94115
            <br />
            Email:{" "}
            <a
              href="mailto:privacy@startupsmartup.com"
              className="text-indigo-600 underline"
            >
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Effective Date: May 27, 2026 — Last Updated: May 27, 2026
        </div>

        {/* ── COPPA Addendum ── */}
        <div id="coppa" className="pt-8 border-t-2 border-indigo-100">
          <h2 className="text-2xl font-bold text-indigo-700 mb-1">
            COPPA Addendum — Children's Online Privacy Protection
          </h2>
          <p className="text-xs text-gray-500 mb-6">
            15 U.S.C. §§ 6501–6506; 16 CFR Part 312 (§§ 312.1, 312.6) &nbsp;|&nbsp; Effective Date: May 27, 2026
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">A. School-Agent Exemption</h3>
              <p className="mb-2">
                Project Explore collects personal information from children
                under 13 only at the direction of an educational institution,
                under the school-agent exemption recognized under the COPPA
                Rule (16 CFR § 312.1). Schools provide the authorization that,
                under this exemption, would otherwise be obtained from parents
                directly. Project Explore does not seek or require separate
                direct parental consent for school-provisioned student accounts.
              </p>
              <p className="mb-3">
                By entering into a contract with Startup Smartup, the school or
                district represents that it has the authority to authorize the
                collection of student data for educational purposes on behalf of
                parents and consents to the collection, use, and disclosure of
                student personal information as described in this addendum and
                in the district's data processing agreement.
              </p>
              <p className="mb-2">
                <strong>Not a consumer service.</strong> Project Explore is not
                a consumer-facing service. Student accounts are provisioned only
                through district or school contracts. There is no path by which
                a student can independently create an account or access the
                platform outside of a school-authorized context.
              </p>
              <p>
                <strong>Fallback consent.</strong> If the school-agent exemption
                is unavailable in a specific circumstance, Project Explore will
                obtain verifiable parental consent before collecting personal
                information from a child under 13, consistent with 16 CFR
                § 312.5.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">B. What Is Collected</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">Category</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">Specific Data</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">Purpose</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Identity</td><td className="border border-gray-200 px-3 py-2">Student first name, last name</td><td className="border border-gray-200 px-3 py-2">Educational delivery, teacher roster view</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Grade</td><td className="border border-gray-200 px-3 py-2">Student grade level (K-5)</td><td className="border border-gray-200 px-3 py-2">Tier assignment, age-appropriate content delivery</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Contact</td><td className="border border-gray-200 px-3 py-2">Student email (grade 3+ only, where provided by district)</td><td className="border border-gray-200 px-3 py-2">Optional: assignment notifications</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Educational activity</td><td className="border border-gray-200 px-3 py-2">Assignment responses and submissions</td><td className="border border-gray-200 px-3 py-2">Progress tracking, teacher review, AI feedback</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Educational activity</td><td className="border border-gray-200 px-3 py-2">Video viewing analytics</td><td className="border border-gray-200 px-3 py-2">Progress tracking, district reporting</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Educational feedback</td><td className="border border-gray-200 px-3 py-2">AI-generated feedback on open-response items</td><td className="border border-gray-200 px-3 py-2">Teacher review, student learning</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Accommodations</td><td className="border border-gray-200 px-3 py-2">IEP/504 flags (where provided by district)</td><td className="border border-gray-200 px-3 py-2">Accessibility scaffolding delivery</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">C. What Is NOT Collected</h3>
              <p className="mb-2">Project Explore does not collect from students:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Social Security numbers</li>
                <li>Home addresses</li>
                <li>Biometric identifiers</li>
                <li>Geolocation data</li>
                <li>Financial information</li>
                <li>Health information beyond IEP/504 flags provided by the school</li>
                <li>Behavioral advertising data or interest-based profiles</li>
                <li>Device identifiers used for advertising</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">D. Why Each Category Is Collected</h3>
              <p className="mb-2">Collection is limited to the following purposes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Educational delivery:</strong> Providing grade-level, bilingual (EN/ES) video content and activities to students</li>
                <li><strong>Progress tracking:</strong> Enabling teachers and administrators to monitor student completion and mastery</li>
                <li><strong>Teacher review:</strong> Enabling teachers to read and respond to student open-response submissions</li>
                <li><strong>AI-generated feedback:</strong> Generating formative feedback on student responses</li>
                <li><strong>District reporting:</strong> Providing completion and progress data to district administrators</li>
                <li><strong>Accessibility scaffolding:</strong> Delivering IEP/504-appropriate content variants where applicable</li>
              </ul>
              <p className="mt-2 font-medium">
                No other purposes. Student data is not used for advertising,
                marketing, product development, or any purpose unrelated to
                educational delivery.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">E. Third-Party Sub-Processors</h3>
              <p className="mb-3">
                Project Explore shares student data only with the following
                processors, each operating under a data processing agreement
                that prohibits use of student data for any purpose other than
                providing the contracted service:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">Vendor</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">Role</th>
                      <th className="border border-gray-200 px-3 py-2 text-left font-medium">DPA / Training</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Supabase</td><td className="border border-gray-200 px-3 py-2">Database hosting, authentication, edge functions</td><td className="border border-gray-200 px-3 py-2">SOC 2 Type II, signed DPA</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Cloudflare (R2 + AutoRAG)</td><td className="border border-gray-200 px-3 py-2">Static asset storage, AI-powered support</td><td className="border border-gray-200 px-3 py-2">SOC 2 Type II, signed DPA</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Vimeo</td><td className="border border-gray-200 px-3 py-2">Video hosting and delivery</td><td className="border border-gray-200 px-3 py-2">SOC 2 Type II, signed DPA, FERPA-aware</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">OpenAI</td><td className="border border-gray-200 px-3 py-2">Open-question grading; TTS narration</td><td className="border border-gray-200 px-3 py-2">DPA in place; API terms prohibit training on student data</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Anthropic</td><td className="border border-gray-200 px-3 py-2">AI support via Claude</td><td className="border border-gray-200 px-3 py-2">DPA in place (Commercial ToS); API terms prohibit training on student data</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Google Cloud</td><td className="border border-gray-200 px-3 py-2">Google Classroom integration (where used)</td><td className="border border-gray-200 px-3 py-2">Standard Google Workspace DPA</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Clever</td><td className="border border-gray-200 px-3 py-2">Student SSO (where used by district)</td><td className="border border-gray-200 px-3 py-2">District-level DPA</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">ElevenLabs</td><td className="border border-gray-200 px-3 py-2">TTS for voice interview (where used)</td><td className="border border-gray-200 px-3 py-2">DPA available</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">Deepgram</td><td className="border border-gray-200 px-3 py-2">STT for voice interview (where used)</td><td className="border border-gray-200 px-3 py-2">DPA available</td></tr>
                    <tr className="even:bg-gray-50"><td className="border border-gray-200 px-3 py-2">SendGrid (Twilio)</td><td className="border border-gray-200 px-3 py-2">Teacher/admin invitation emails only</td><td className="border border-gray-200 px-3 py-2">SOC 2 Type II; DPA via Twilio</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2">No student data is sold, rented, or shared with advertising networks.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">F. Data Retention</h3>
              <p className="mb-2">
                Student personal information is retained for the duration of
                the student's active enrollment in a Project Explore class.
                Project Explore will delete inactive student personal
                information no later than 90 days following the end of the
                enrollment period.
              </p>
              <p className="mb-2">
                At the conclusion of the retention period, student data is
                deleted from production systems. Backup copies are purged on a
                rolling 30-day cycle and fully deleted within 30 days after the
                data is removed from production.
              </p>
              <p>
                Audit logs are retained for 7 years as required by certain
                state regulations.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">
                G. Parental Rights Under COPPA § 312.6
              </h3>
              <p className="mb-2">
                Parents and legal guardians of children under 13 have the right
                to review, correct, delete, or refuse further collection of
                their child's personal information.
              </p>
              <p className="mb-2">
                <strong>Process:</strong> Parents may exercise these rights by
                contacting their child's school administrator, who can fulfill
                the request through the teacher dashboard. Alternatively,
                parents may contact Project Explore directly at{" "}
                <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                  privacy@startupsmartup.com
                </a>
                . Project Explore will verify the parent's identity through the
                contracting school. Requests will be fulfilled within 45 days.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">H. No Behavioral Advertising</h3>
              <p>
                Project Explore does not display behavioral advertising to
                students, does not use student data to build advertising
                profiles, and does not share student data with advertising
                networks or data brokers.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">I. COPPA Contact</h3>
              <p>
                <strong>Justin Ghiglia, Privacy Contact</strong>
                <br />
                Startup Smartup
                <br />
                2380 California Street #402, San Francisco, CA 94115
                <br />
                <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                  privacy@startupsmartup.com
                </a>
              </p>
              <p className="mt-3 text-xs text-gray-500">
                Cross-reference:{" "}
                <Link to="/ferpa" className="text-indigo-600 underline">FERPA Statement</Link>{" | "}
                <Link to="/legal/sopipa" className="text-indigo-600 underline">SOPIPA Acknowledgment</Link>{" | "}
                <Link to="/legal/dpa" className="text-indigo-600 underline">Data Processing Agreement</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const TH = "border border-gray-200 px-3 py-2 text-left font-medium";
const TD = "border border-gray-200 px-3 py-2";

function PrivacyPolicyPageEs() {
  return (
    <div className="bg-gray-50 min-h-screen pt-20">
      <section className="bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-800 py-20 px-4 text-white text-center">
        <h1 className="text-4xl font-bold mb-2">Política de privacidad</h1>
        <p className="text-lg max-w-2xl mx-auto">
          Proteger la privacidad de los estudiantes es nuestra prioridad. Esta
          política explica cómo Project Explore recopila, usa y protege la
          información personal.
        </p>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto space-y-10 text-gray-700 text-sm">
        <p className="p-3 bg-indigo-50 border-l-4 border-indigo-500 rounded text-indigo-800">
          Esta página es una traducción al español. Para ver el texto original en inglés, elige EN arriba.
        </p>
        <p className="text-xs text-gray-500">
          <strong>Operador:</strong> Startup Smartup, 2380 California Street
          #402, San Francisco, CA 94115 &nbsp;|&nbsp;{" "}
          <strong>Fecha de entrada en vigor:</strong> 27 de mayo de 2026
        </p>

        {/* Section 1 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            1. Información que recopilamos
          </h2>
          <p className="mb-3">
            Project Explore es una plataforma educativa bilingüe para K-5 que
            funciona bajo contrato con escuelas y distritos. Todas las cuentas
            de estudiantes las crea la escuela o el distrito contratante. Los
            estudiantes no se registran por su cuenta con información personal.
          </p>
          <p className="font-medium mb-2">Información de los estudiantes que recopilamos:</p>
          <div className="overflow-x-auto mb-4">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-gray-100">
                <tr>
                  <th className={TH}>Categoría</th>
                  <th className={TH}>Datos</th>
                  <th className={TH}>Notas</th>
                </tr>
              </thead>
              <tbody>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Identidad</td>
                  <td className={TD}>Nombre y apellido</td>
                  <td className={TD}>Obligatorio para todas las cuentas de estudiantes</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Matrícula</td>
                  <td className={TD}>Grado escolar (K-5)</td>
                  <td className={TD}>Obligatorio; se usa para asignar los niveles de contenido adecuados</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Contacto</td>
                  <td className={TD}>Correo electrónico</td>
                  <td className={TD}>Se recopila de estudiantes de 3.er grado en adelante, solo cuando lo proporciona el distrito</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Actividad educativa</td>
                  <td className={TD}>Respuestas y trabajos entregados</td>
                  <td className={TD}>Se usan para dar seguimiento al progreso y para la revisión del maestro</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Actividad educativa</td>
                  <td className={TD}>Estadísticas de reproducción de video</td>
                  <td className={TD}>Se usan para dar seguimiento al progreso y para los informes del distrito</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Retroalimentación educativa</td>
                  <td className={TD}>Comentarios generados por IA sobre preguntas de respuesta abierta</td>
                  <td className={TD}>Vinculados a los trabajos entregados; los revisan los maestros</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Adaptaciones</td>
                  <td className={TD}>Indicadores de IEP/504</td>
                  <td className={TD}>Los proporciona el distrito; se usan para ofrecer apoyos de accesibilidad</td>
                </tr>
                <tr className="even:bg-gray-50">
                  <td className={TD}>Padre, madre o tutor</td>
                  <td className={TD}>Información de contacto</td>
                  <td className={TD}>Cuando la proporciona el distrito, para fines de notificación</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="font-medium mb-2">Información de maestros y administradores que recopilamos:</p>
          <p className="mb-3">
            Nombre y correo electrónico profesional, para crear la cuenta,
            administrar las clases y comunicarnos por la plataforma.
          </p>
          <p className="font-medium mb-2">Lo que NO recopilamos de los estudiantes:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Números de Seguro Social</li>
            <li>Domicilios</li>
            <li>Identificadores biométricos</li>
            <li>Datos de geolocalización</li>
            <li>Información financiera</li>
            <li>Información de salud, aparte de los indicadores de IEP/504 que proporciona la escuela</li>
            <li>Datos de publicidad conductual o perfiles basados en intereses</li>
            <li>Identificadores de dispositivos usados para publicidad</li>
          </ul>
        </div>

        {/* Section 2 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            2. Cómo usamos la información
          </h2>
          <p className="mb-3">
            Los datos de los estudiantes se usan únicamente para ofrecer la
            experiencia educativa de Project Explore. En concreto:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li><strong>Enseñanza:</strong> Ofrecer a los estudiantes videos y actividades bilingües (inglés y español) para su grado</li>
            <li><strong>Seguimiento del progreso:</strong> Permitir que maestros y administradores vean qué han completado y dominado los estudiantes</li>
            <li><strong>Revisión del maestro:</strong> Permitir que los maestros lean y respondan los trabajos de respuesta abierta de los estudiantes</li>
            <li><strong>Comentarios generados por IA:</strong> Generar comentarios formativos sobre las respuestas de los estudiantes con las funciones de IA de la plataforma</li>
            <li><strong>Informes del distrito:</strong> Entregar a los administradores del distrito datos de avance y finalización para evaluar el programa</li>
            <li><strong>Apoyos de accesibilidad:</strong> Ofrecer versiones del contenido adecuadas para IEP/504 cuando corresponda</li>
          </ul>
          <p className="font-semibold">
            Los datos de los estudiantes nunca se usan para publicidad,
            mercadotecnia, desarrollo de productos ni ningún fin ajeno a la
            enseñanza.
          </p>
        </div>

        {/* Section 3 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            3. Cómo compartimos información con terceros
          </h2>
          <p className="mb-3">
            Project Explore no vende, no alquila ni comparte datos de
            estudiantes con terceros con fines comerciales. Los datos de los
            estudiantes se comparten solo con los siguientes procesadores, y
            cada uno opera bajo un acuerdo de procesamiento de datos que
            prohíbe usar esos datos para cualquier fin distinto de prestar el
            servicio contratado:
          </p>
          <ul className="list-disc list-inside space-y-2 ml-2 mb-4">
            <li><strong>Supabase</strong>: alojamiento de bases de datos, autenticación y funciones sin servidor</li>
            <li><strong>Cloudflare (R2 + AutoRAG)</strong>: almacenamiento de archivos estáticos y asistencia con IA</li>
            <li><strong>Vimeo</strong>: alojamiento y entrega de videos</li>
            <li><strong>OpenAI</strong>: calificación de preguntas abiertas y narración de texto a voz. Hay un DPA vigente; los términos de la API prohíben usar los datos del cliente, incluida la información personal de los estudiantes, para entrenar modelos. No se conserva información personal de los estudiantes más allá de lo que dura la solicitud a la API.</li>
            <li><strong>Anthropic</strong>: funciones de asistencia con IA mediante Claude. Hay un DPA vigente (incorporado a los Términos Comerciales de Servicio); los términos de la API prohíben usar los datos del cliente, incluida la información personal de los estudiantes, para entrenar modelos. No se conserva información personal de los estudiantes más allá de lo que dura la solicitud a la API.</li>
            <li><strong>Google Cloud</strong>: integración con Google Classroom, cuando la usa un distrito</li>
            <li><strong>Clever</strong>: inicio de sesión único para estudiantes, cuando lo usa un distrito</li>
            <li><strong>SendGrid (Twilio SendGrid)</strong>: correos transaccionales para las invitaciones a maestros y administradores (no se usa para correos de estudiantes)</li>
            <li><strong>ElevenLabs, Deepgram, LiveKit</strong>: función de entrevista por voz, cuando se usa</li>
          </ul>
          <p className="mb-4">
            También devolvemos datos a la escuela y al distrito contratantes.
          </p>
          <p className="mb-2">
            <strong>Solicitudes de las autoridades:</strong> Project Explore
            responde solo a trámites legales válidos (citatorio, orden judicial
            u otro requisito legal obligatorio). Cuando la ley lo permita,
            Project Explore avisará al distrito contratante antes de revelar
            cualquier información personal de estudiantes en respuesta a esa
            solicitud, y revelará solo la información mínima necesaria.
          </p>
          <p>
            <strong>Ubicación de los datos:</strong> Toda la información
            personal de los estudiantes se almacena dentro de Estados Unidos.
            Los principales proveedores de infraestructura de Project Explore
            (Supabase, Cloudflare y Vimeo) mantienen centros de datos en
            Estados Unidos para la implementación de Project Explore.
          </p>
        </div>

        {/* Section 4 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            4. Derechos de los padres y solicitudes de eliminación de datos
          </h2>
          <p className="mb-3">
            Los padres y tutores pueden pedir en cualquier momento que se
            revisen, corrijan o eliminen los datos de su hijo, de estas formas:
          </p>
          <ol className="list-decimal list-inside space-y-2 ml-2 mb-3">
            <li>Comunicándose con el administrador de la escuela de su hijo, quien puede exportar o eliminar los registros desde el panel del maestro; o</li>
            <li>Comunicándose directamente con el soporte de Project Explore en{" "}
              <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                privacy@startupsmartup.com
              </a>
            </li>
          </ol>
          <p>
            Project Explore verificará la identidad de quien hace la solicitud
            por medio de la escuela contratante antes de actuar. Las solicitudes
            se cumplirán en un plazo de 45 días, salvo que la ley exija
            conservar los datos.
          </p>
        </div>

        {/* Section 5 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            5. Política de conservación de datos
          </h2>
          <p className="mb-3">
            Los datos de los estudiantes se conservan mientras el estudiante
            siga inscrito en una clase de Project Explore. Project Explore
            eliminará los datos de estudiantes inactivos en un plazo de 90 días
            después de que termine el año escolar, en el caso de los
            estudiantes que ya no estén inscritos.
          </p>
          <p className="mb-3">
            Los datos de los maestros se conservan mientras el maestro esté
            activo en cualquier distrito y se eliminan 365 días después de su
            última actividad registrada.
          </p>
          <p className="mb-3">
            Los registros de auditoría se conservan durante 7 años, como exigen
            ciertos requisitos de cumplimiento de distritos y estados. Las
            copias de seguridad se mantienen en un ciclo continuo de 30 días y
            se eliminan en un plazo de 30 días después de que los datos
            correspondientes se retiran de producción.
          </p>
          <p>
            <strong>Terminación del contrato:</strong> Al terminar el contrato
            con el distrito, Project Explore, según lo que elija el Distrito,
            le devolverá toda la información personal de los estudiantes o la
            eliminará de forma segura en un plazo de treinta (30) días después
            de la terminación. Los distritos pueden pedir un certificado de
            eliminación que confirme que toda la información personal de los
            estudiantes se retiró de los sistemas de Project Explore.
          </p>
        </div>

        {/* Section 6 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            6. Plan de respuesta ante una filtración
          </h2>
          <p className="mb-3">
            Si ocurre una filtración de datos, Project Explore hará lo siguiente:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li>Investigar el origen y el alcance de la filtración</li>
            <li>Contener el incidente protegiendo los sistemas afectados y revocando las credenciales comprometidas</li>
            <li>Probar la corrección para asegurarse de que la vulnerabilidad quedó resuelta</li>
            <li>Avisar a los distritos afectados y, por medio de ellos, a las familias afectadas, en un plazo de 72 horas después de confirmar una filtración</li>
            <li>Notificar a las autoridades reguladoras que correspondan, como exige la ley</li>
            <li>Realizar la limpieza y las mejoras de seguridad a largo plazo</li>
            <li>Publicar un informe posterior al incidente en un plazo de 30 días después de cerrarlo</li>
          </ul>
          <p>
            Consulta la{" "}
            <Link to="/legal/incident-response" className="text-indigo-600 underline">
              Política de respuesta a incidentes
            </Link>{" "}
            completa para ver los detalles.
          </p>
        </div>

        {/* Section 7 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            7. Seguridad de tu información
          </h2>
          <ul className="list-disc list-inside space-y-1 ml-2 mb-3">
            <li><strong>Cifrado en reposo:</strong> Todos los datos de los estudiantes se cifran en reposo con AES-256</li>
            <li><strong>Cifrado en tránsito:</strong> Se exige TLS 1.2 o superior en todas las conexiones</li>
            <li><strong>Controles de acceso:</strong> Las políticas de seguridad a nivel de fila de la base de datos separan los datos por distrito, escuela, clase y estudiante, y no es posible el acceso a datos de otros clientes</li>
            <li><strong>Autenticación:</strong> Se exige autenticación de varios factores a los administradores del distrito y de la plataforma</li>
            <li><strong>Copias de seguridad:</strong> Copias continuas de 30 días con capacidad de recuperación a un punto en el tiempo</li>
          </ul>
          <p>
            Ninguna plataforma puede garantizar el 100% de seguridad, pero nos
            comprometemos a proteger los datos de los estudiantes con prácticas
            estándar de la industria, adecuadas a lo delicado de los
            expedientes educativos.
          </p>
        </div>

        {/* Section 8 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            8. Información que se recopila automáticamente
          </h2>
          <p className="mb-2">
            Project Explore no recopila automáticamente información sobre tu
            dispositivo, navegador o ubicación con fines de publicidad o de
            creación de perfiles. No usamos píxeles publicitarios, scripts de
            seguimiento conductual ni integraciones con corredores de datos.
          </p>
          <p>
            Los registros técnicos de la plataforma (registros de errores,
            eventos de autenticación, solicitudes a funciones de borde) se
            recopilan solo para el monitoreo operativo y de seguridad, y no se
            vinculan a perfiles de identidad de estudiantes.
          </p>
        </div>

        {/* Section 9 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            9. Seguimiento del comportamiento de los usuarios
          </h2>
          <p>
            Nuestra plataforma no da seguimiento al comportamiento de los
            usuarios para publicidad, personalización de publicidad ni análisis
            conductual. Todos los datos de actividad que se recopilan (avance
            en los videos, tareas completadas, calificaciones de evaluaciones)
            se usan exclusivamente para la enseñanza y para los informes a la
            escuela y al distrito contratantes.
          </p>
        </div>

        {/* Section 10 */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            10. Cambios a esta política
          </h2>
          <p>
            Podemos actualizar esta Política de privacidad de vez en cuando.
            Los cambios importantes se anunciarán en la plataforma con al menos
            30 días de aviso antes de que entren en vigor. Seguir usando la
            plataforma después de la fecha de entrada en vigor significa que
            aceptas la política revisada. Mantenemos un historial de los
            cambios importantes en nuestro Registro de cambios de
            cumplimiento.
          </p>
        </div>

        {/* Additional Compliance Statements */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            Declaraciones de cumplimiento adicionales
          </h2>
          <p className="mb-2">
            Project Explore mantiene declaraciones de cumplimiento por
            separado para requisitos federales y estatales específicos:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>
              <Link to="/privacy#coppa" className="text-indigo-600 underline">
                Anexo de COPPA
              </Link>
              : Ley de Protección de la Privacidad de los Niños en Internet
            </li>
            <li>
              <Link to="/ferpa" className="text-indigo-600 underline">
                Declaración de FERPA
              </Link>
              : Ley de Derechos Educativos y Privacidad de la Familia
            </li>
            <li>
              <Link to="/legal/sopipa" className="text-indigo-600 underline">
                Reconocimiento de SOPIPA de California
              </Link>
              : Código de Negocios y Profesiones de California, §§ 22584 y 22585
            </li>
            <li>
              <Link to="/acceptable-use" className="text-indigo-600 underline">
                Política de uso aceptable
              </Link>
            </li>
            <li>
              <Link to="/legal/incident-response" className="text-indigo-600 underline">
                Política de respuesta a incidentes
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <h2 className="text-xl font-semibold text-indigo-700 mb-3">
            Información de contacto
          </h2>
          <p className="mb-2">
            Si tienes preguntas o inquietudes sobre nuestras prácticas de
            privacidad, comunícate con:
          </p>
          <p>
            <strong>Justin Ghiglia</strong>
            <br />
            Project Explore / Startup Smartup
            <br />
            2380 California Street, #402
            <br />
            San Francisco, CA 94115
            <br />
            Correo electrónico:{" "}
            <a
              href="mailto:privacy@startupsmartup.com"
              className="text-indigo-600 underline"
            >
              privacy@startupsmartup.com
            </a>
          </p>
        </div>

        <div className="text-xs text-gray-500">
          Fecha de entrada en vigor: 27 de mayo de 2026. Última actualización: 27 de mayo de 2026
        </div>

        {/* ── COPPA Addendum ── */}
        <div id="coppa" className="pt-8 border-t-2 border-indigo-100">
          <h2 className="text-2xl font-bold text-indigo-700 mb-1">
            Anexo de COPPA: Protección de la privacidad de los niños en internet
          </h2>
          <p className="text-xs text-gray-500 mb-6">
            15 U.S.C. §§ 6501 a 6506; 16 CFR Parte 312 (§§ 312.1, 312.6) &nbsp;|&nbsp; Fecha de entrada en vigor: 27 de mayo de 2026
          </p>

          <div className="space-y-8">
            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">A. Excepción del agente escolar</h3>
              <p className="mb-2">
                Project Explore recopila información personal de niños menores
                de 13 años solo por indicación de una institución educativa, bajo
                la excepción del agente escolar que reconoce la Regla de COPPA
                (16 CFR § 312.1). Las escuelas dan la autorización que, bajo esta
                excepción, de otro modo se pediría directamente a los padres.
                Project Explore no busca ni exige un consentimiento directo y
                aparte de los padres para las cuentas de estudiantes que crea la
                escuela.
              </p>
              <p className="mb-3">
                Al firmar un contrato con Startup Smartup, la escuela o el
                distrito declara que tiene la autoridad para autorizar la
                recopilación de datos de los estudiantes con fines educativos en
                nombre de los padres, y da su consentimiento para la
                recopilación, el uso y la divulgación de la información personal
                de los estudiantes como se describe en este anexo y en el
                acuerdo de procesamiento de datos del distrito.
              </p>
              <p className="mb-2">
                <strong>No es un servicio para el consumidor.</strong> Project
                Explore no es un servicio dirigido al consumidor. Las cuentas de
                estudiantes se crean solo mediante contratos con distritos o
                escuelas. Un estudiante no tiene forma de crear una cuenta por su
                cuenta ni de entrar a la plataforma fuera de un contexto
                autorizado por la escuela.
              </p>
              <p>
                <strong>Consentimiento de respaldo.</strong> Si la excepción del
                agente escolar no está disponible en una circunstancia
                específica, Project Explore obtendrá el consentimiento parental
                verificable antes de recopilar información personal de un niño
                menor de 13 años, de acuerdo con 16 CFR § 312.5.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">B. Qué se recopila</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className={TH}>Categoría</th>
                      <th className={TH}>Datos específicos</th>
                      <th className={TH}>Finalidad</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="even:bg-gray-50"><td className={TD}>Identidad</td><td className={TD}>Nombre y apellido del estudiante</td><td className={TD}>Enseñanza, lista de estudiantes que ve el maestro</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Grado</td><td className={TD}>Grado escolar del estudiante (K-5)</td><td className={TD}>Asignación de nivel, contenido adecuado para la edad</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Contacto</td><td className={TD}>Correo electrónico del estudiante (solo de 3.er grado en adelante, cuando lo proporciona el distrito)</td><td className={TD}>Opcional: avisos de tareas</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Actividad educativa</td><td className={TD}>Respuestas y trabajos entregados</td><td className={TD}>Seguimiento del progreso, revisión del maestro, comentarios de IA</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Actividad educativa</td><td className={TD}>Estadísticas de reproducción de video</td><td className={TD}>Seguimiento del progreso, informes del distrito</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Retroalimentación educativa</td><td className={TD}>Comentarios generados por IA sobre preguntas de respuesta abierta</td><td className={TD}>Revisión del maestro, aprendizaje del estudiante</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Adaptaciones</td><td className={TD}>Indicadores de IEP/504 (cuando los proporciona el distrito)</td><td className={TD}>Entrega de apoyos de accesibilidad</td></tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">C. Qué NO se recopila</h3>
              <p className="mb-2">Project Explore no recopila de los estudiantes:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Números de Seguro Social</li>
                <li>Domicilios</li>
                <li>Identificadores biométricos</li>
                <li>Datos de geolocalización</li>
                <li>Información financiera</li>
                <li>Información de salud, aparte de los indicadores de IEP/504 que proporciona la escuela</li>
                <li>Datos de publicidad conductual o perfiles basados en intereses</li>
                <li>Identificadores de dispositivos usados para publicidad</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">D. Por qué se recopila cada categoría</h3>
              <p className="mb-2">La recopilación se limita a los siguientes fines:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong>Enseñanza:</strong> Ofrecer a los estudiantes videos y actividades bilingües (EN/ES) para su grado</li>
                <li><strong>Seguimiento del progreso:</strong> Permitir que maestros y administradores vean qué han completado y dominado los estudiantes</li>
                <li><strong>Revisión del maestro:</strong> Permitir que los maestros lean y respondan los trabajos de respuesta abierta de los estudiantes</li>
                <li><strong>Comentarios generados por IA:</strong> Generar comentarios formativos sobre las respuestas de los estudiantes</li>
                <li><strong>Informes del distrito:</strong> Entregar a los administradores del distrito datos de avance y finalización</li>
                <li><strong>Apoyos de accesibilidad:</strong> Ofrecer versiones del contenido adecuadas para IEP/504 cuando corresponda</li>
              </ul>
              <p className="mt-2 font-medium">
                Ningún otro fin. Los datos de los estudiantes no se usan para
                publicidad, mercadotecnia, desarrollo de productos ni ningún fin
                ajeno a la enseñanza.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">E. Subprocesadores externos</h3>
              <p className="mb-3">
                Project Explore comparte datos de estudiantes solo con los
                siguientes procesadores, y cada uno opera bajo un acuerdo de
                procesamiento de datos que prohíbe usar esos datos para
                cualquier fin distinto de prestar el servicio contratado:
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className={TH}>Proveedor</th>
                      <th className={TH}>Función</th>
                      <th className={TH}>DPA / Entrenamiento</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="even:bg-gray-50"><td className={TD}>Supabase</td><td className={TD}>Alojamiento de bases de datos, autenticación, funciones de borde</td><td className={TD}>SOC 2 Tipo II, DPA firmado</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Cloudflare (R2 + AutoRAG)</td><td className={TD}>Almacenamiento de archivos estáticos, asistencia con IA</td><td className={TD}>SOC 2 Tipo II, DPA firmado</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Vimeo</td><td className={TD}>Alojamiento y entrega de videos</td><td className={TD}>SOC 2 Tipo II, DPA firmado, consciente de FERPA</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>OpenAI</td><td className={TD}>Calificación de preguntas abiertas; narración de texto a voz</td><td className={TD}>DPA vigente; los términos de la API prohíben entrenar con datos de estudiantes</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Anthropic</td><td className={TD}>Asistencia con IA mediante Claude</td><td className={TD}>DPA vigente (Términos Comerciales); los términos de la API prohíben entrenar con datos de estudiantes</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Google Cloud</td><td className={TD}>Integración con Google Classroom (cuando se usa)</td><td className={TD}>DPA estándar de Google Workspace</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Clever</td><td className={TD}>Inicio de sesión único para estudiantes (cuando lo usa el distrito)</td><td className={TD}>DPA a nivel de distrito</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>ElevenLabs</td><td className={TD}>Texto a voz para la entrevista por voz (cuando se usa)</td><td className={TD}>DPA disponible</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>Deepgram</td><td className={TD}>Voz a texto para la entrevista por voz (cuando se usa)</td><td className={TD}>DPA disponible</td></tr>
                    <tr className="even:bg-gray-50"><td className={TD}>SendGrid (Twilio)</td><td className={TD}>Solo correos de invitación a maestros y administradores</td><td className={TD}>SOC 2 Tipo II; DPA mediante Twilio</td></tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-2">No se vende, alquila ni comparte ningún dato de estudiantes con redes de publicidad.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">F. Conservación de datos</h3>
              <p className="mb-2">
                La información personal de los estudiantes se conserva mientras
                el estudiante siga inscrito en una clase de Project Explore.
                Project Explore eliminará la información personal de estudiantes
                inactivos a más tardar 90 días después de que termine el período
                de inscripción.
              </p>
              <p className="mb-2">
                Al concluir el período de conservación, los datos de los
                estudiantes se eliminan de los sistemas de producción. Las copias
                de seguridad se eliminan en un ciclo continuo de 30 días y se
                borran por completo en un plazo de 30 días después de que los
                datos se retiran de producción.
              </p>
              <p>
                Los registros de auditoría se conservan durante 7 años, como
                exigen ciertas normas estatales.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">
                G. Derechos de los padres bajo COPPA § 312.6
              </h3>
              <p className="mb-2">
                Los padres y tutores legales de niños menores de 13 años tienen
                derecho a revisar, corregir y eliminar la información personal
                de su hijo, o a negarse a que se siga recopilando.
              </p>
              <p className="mb-2">
                <strong>Proceso:</strong> Los padres pueden ejercer estos
                derechos comunicándose con el administrador de la escuela de su
                hijo, quien puede atender la solicitud desde el panel del
                maestro. También pueden comunicarse directamente con Project
                Explore en{" "}
                <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                  privacy@startupsmartup.com
                </a>
                . Project Explore verificará la identidad del padre por medio de
                la escuela contratante. Las solicitudes se cumplirán en un plazo
                de 45 días.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">H. Sin publicidad conductual</h3>
              <p>
                Project Explore no muestra publicidad conductual a los
                estudiantes, no usa datos de estudiantes para crear perfiles
                publicitarios y no comparte datos de estudiantes con redes de
                publicidad ni con corredores de datos.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-indigo-700 mb-2">I. Contacto de COPPA</h3>
              <p>
                <strong>Justin Ghiglia, contacto de privacidad</strong>
                <br />
                Startup Smartup
                <br />
                2380 California Street #402, San Francisco, CA 94115
                <br />
                <a href="mailto:privacy@startupsmartup.com" className="text-indigo-600 underline">
                  privacy@startupsmartup.com
                </a>
              </p>
              <p className="mt-3 text-xs text-gray-500">
                Referencias cruzadas:{" "}
                <Link to="/ferpa" className="text-indigo-600 underline">Declaración de FERPA</Link>{" | "}
                <Link to="/legal/sopipa" className="text-indigo-600 underline">Reconocimiento de SOPIPA</Link>{" | "}
                <Link to="/legal/dpa" className="text-indigo-600 underline">Acuerdo de procesamiento de datos</Link>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  return useUiLang() === "es" ? <PrivacyPolicyPageEs /> : <PrivacyPolicyPageEn />;
}
